import * as Sentry from '@sentry/node';
import { CardElement } from '@stripe/react-stripe-js';
import { FormikBag } from 'formik';
import Cookies from 'js-cookie';
import Router from 'next/router';

import { TCheckoutFormProps } from '@jpp/organisms/CheckoutForm';
import { ICheckoutFormValues } from '@jpp/organisms/CheckoutForm/types';
import { IAuthStoreState } from 'common/redux/auth/reducer';
import { IIntentResponse, IPaymentIntentAPI } from 'common/redux/payment/types';
import {
  ICartLineItem,
  ICartShippingMethod,
} from 'common/redux/wooCommerce/cart/reducer';
import { EPageType } from 'common/typings/enums';
import axios from 'common/utils/axios/internal';
import { GHBC_CHECKOUT_FORM } from 'common/utils/constants';
import {
  GHBC_BASKET_ID,
  GHBC_DEFAULT,
  GHBC_UUID,
} from 'common/utils/constants/cookies';
import { PAYMENT_METHOD_NEW_CARD } from 'common/utils/constants/stripe';
import {
  getSubscriptionProducts,
  hasOnlyVirtualProductsInCart,
  hasSubscriptionProducts,
} from 'common/utils/shared/cart';
import {
  getCheckoutBillingAndShipping,
  getStripeBilling,
  getStripeShipping,
  ISubmitOrder,
  ISubmitOrderShippingLine,
  mapCheckoutFieldsToOrder,
} from 'common/utils/shared/orders';
import {
  stripeCartLineItem,
  stripeCheckoutCartLineItem,
} from 'common/utils/shared/stripe';
import {
  IWooCommerceAPIBilling,
  IWooCommerceAPIOrder,
  IWooCommerceAPIShipping,
} from 'common/utils/transformers/order.transformer';

import { IWithStripeElements } from '../../../hoc/withStripe';
import ConfigProvider from '../../../services/configProvider';

interface IPaymentUserData extends IWithStripeElements {
  email: string;
  orderId: number;
  customer_name: string;
  user: string;
  paymentMethod?: string;
  paymentIntent?: string | null;
  billing: IWooCommerceAPIBilling;
  shipping?: IWooCommerceAPIShipping;
}

const errorMessageField = 'errorMessage';
const paymentErrorMessageField = 'paymentErrorMessage';

const handleSaveCard = async (
  values: ICheckoutFormValues,
  hasOnlyVirtualProducts: boolean,
  stripe: IWithStripeElements['stripe'],
  elements: IWithStripeElements['elements'],
  shipping: IWooCommerceAPIShipping,
  billing: IWooCommerceAPIBilling
) => {
  try {
    const firstName = values.billing_first_name || values.shipping_first_name;
    const lastName = values.billing_last_name || values.shipping_last_name;
    const data = {
      customer_email: values.email,
      customer_name: `${firstName} ${lastName}`,
      customer_phone: values.phone,
      shipping: hasOnlyVirtualProducts ? undefined : shipping,
    };
    const { data: saveCardIntent } = await axios.post<IIntentResponse>(
      '/payment/setup-wallet',
      data
    );
    const result = await stripe.confirmCardSetup(saveCardIntent.client_secret, {
      payment_method: {
        card: elements.getElement(CardElement)!,
        billing_details: getStripeBilling(shipping || billing),
      },
    });
    if (result.error) {
      Sentry.captureException(result.error);
    } else {
      // Send the success card to wordpress to store against the customer record
      // _stripe_customer_id
      // _stripe_card_id
    }
  } catch (error) {
    Sentry.captureException(error);
    return;
  }
};

export const getPaymentIntent = async (
  values: ICheckoutFormValues,
  cartLineItems: ICartLineItem[],
  shippingMethod: ISubmitOrderShippingLine,
  hasOnlyVirtualProducts: boolean,
  orderId: number
): Promise<IIntentResponse | undefined> => {
  try {
    const firstName = values.billing_first_name || values.shipping_first_name;
    const lastName = values.billing_last_name || values.shipping_last_name;
    const line_items = await stripeCartLineItem(cartLineItems, shippingMethod);
    const { shipping } = getCheckoutBillingAndShipping(values);

    const data: IPaymentIntentAPI = {
      line_items,
      order_id: orderId,
      customer_email: values.email,
      customer_name: `${firstName} ${lastName}`,
      customer_phone: values.phone,
      payment_method: values.savedPaymentMethod,
      shipping: hasOnlyVirtualProducts ? undefined : shipping,
      metadata: {
        checkout_type: 'Standard Checkout',
      },
    };
    const { data: paymentIntentSecret } = await axios.post<IIntentResponse>(
      '/payment/intent',
      data
    );
    if (!paymentIntentSecret) {
      throw Error('No payment intent secret');
    }
    return paymentIntentSecret;
  } catch (e) {
    Sentry.captureException(e);
    return;
  }
};

export const handleCheckoutFormPayment = async (
  cartLineItems: ICartLineItem[],
  shippingMethod: ISubmitOrderShippingLine,
  { processOrder, processSubscription, cancelOrder }: TCheckoutFormProps,
  {
    email,
    orderId,
    customer_name,
    billing,
    shipping,
    elements,
    stripe,
    paymentIntent,
    paymentMethod,
    user,
  }: IPaymentUserData,
  setFieldError: (field: string, message: string) => void,
  setStatus: (status?: any) => void
) => {
  const isFullStripeIntegration =
    ConfigProvider.getValue('STRIPE_FULL_INTEGRATION') === 'true';

  if (!isFullStripeIntegration) {
    const line_items = await stripeCheckoutCartLineItem(
      cartLineItems,
      shippingMethod
    );
    const { data: session } = await axios.post('/payment/stripe-checkout', {
      line_items,
      customer_email: email,
      client_reference_id: orderId,
      customer_name,
      order_id: orderId,
      shipping: shipping || billing,
    });
    const { error } = await stripe!.redirectToCheckout({
      sessionId: session.id,
    });
    if (error) {
      if (error?.message) {
        setFieldError(errorMessageField, error.message);
      }
      await cancelOrder(String(orderId));
      Sentry.captureException(error);
    }
    return;
  }

  if (isFullStripeIntegration && stripe && elements && paymentIntent) {
    const hasSavedMethod = paymentMethod !== PAYMENT_METHOD_NEW_CARD;
    const successUrl = `/${EPageType.Checkout}/${EPageType.OrderConfirmation}?order_id_success=${orderId}&user=${user}&session_id=stripe_payment`;

    const { error, ...result } = await stripe.confirmCardPayment(
      paymentIntent,
      {
        payment_method: hasSavedMethod
          ? paymentMethod
          : {
              card: elements.getElement(CardElement)!,
              billing_details: getStripeBilling(shipping || billing),
            },
        shipping: getStripeShipping(shipping),
        save_payment_method: false,
        return_url: `${ConfigProvider.getValue('SITE_URL')}/${successUrl}`,
      }
    );

    if (error) {
      await cancelOrder(String(orderId));
      setFieldError(
        paymentErrorMessageField,
        error.message || 'Something went wrong'
      );
      return;
    }

    if (result?.paymentIntent?.status === 'succeeded') {
      await processOrder(String(orderId));
      setStatus('Payment complete');
      Router.push(successUrl);
      return;
    }
    return;
  }
};

export const handleOrderCreation = async (
  formData: ISubmitOrder
): Promise<IWooCommerceAPIOrder> => {
  const { data } = await axios.post('/order', formData).then((res) => res);
  return data;
};

const handleMailchimpSignup = async ({
  first_name,
  last_name,
  email,
}: IWooCommerceAPIBilling) => {
  try {
    await axios.post<string>('/mailchimp', {
      first_name,
      last_name,
      email,
    });
  } catch (e) {
    Sentry.captureException(e);
  }
};

const handleSubscriptions = async (
  processSubscription: TCheckoutFormProps['processSubscription'],
  subscriptionLineItems: ICartLineItem[],
  authUser: TCheckoutFormProps['authUser'],
  stripe: TCheckoutFormProps['stripe'],
  elements: TCheckoutFormProps['elements'],
  shipping: IWooCommerceAPIShipping,
  billing: IWooCommerceAPIBilling
) => {
  const { stripe_data } = authUser || ({} as IAuthStoreState);
  const { id } = stripe_data || {};
  const card = elements.getElement(CardElement)!;
  const { token, error } = await stripe.createToken(card);
  if (error || !id || !token) {
    throw new Error('Subscription token failed');
  }
  const source = token.id;
  const { data } = await axios.post(`/customer/source/${id}/${source}`);
  const cardId = token?.card?.id || data.id;
  await processSubscription(
    subscriptionLineItems,
    cardId,
    authUser,
    shipping,
    billing
  );
};

export const handleCheckoutFormSubmit = async (
  values: ICheckoutFormValues,
  {
    props,
    setFieldError,
    setStatus,
  }: FormikBag<TCheckoutFormProps, ICheckoutFormValues>
) => {
  const {
    selectedShippingMethod,
    cartLineItems,
    authUser,
    coupons,
    processOrder,
    processSubscription,
    total,
    elements,
    stripe,
  } = props;
  const isFullStripeIntegration =
    ConfigProvider.getValue('STRIPE_FULL_INTEGRATION') === 'true';
  const isAuthEnabled = ConfigProvider.getValue('AUTH_ENABLED') === 'true';
  const hasOnlyVirtualProducts = hasOnlyVirtualProductsInCart(cartLineItems);
  const hasSubscriptionProds = hasSubscriptionProducts(cartLineItems);
  const isPaymentRequired = total > 0;
  const isOptedIn = values.marketing_opt_in;
  const noDeliveryMethod: Pick<
    ICartShippingMethod,
    'method_id' | 'label' | 'cost' | 'taxes'
  > = {
    method_id: 'no-delivery',
    label: 'no-delivery',
    cost: '0',
    taxes: { '0': 0 },
  };
  const basketId = Cookies.get(GHBC_BASKET_ID);
  const uuid = Cookies.get(GHBC_UUID);
  const user = basketId || uuid || GHBC_DEFAULT;

  if (!stripe || !elements) {
    return;
  }

  try {
    if (!selectedShippingMethod && !hasOnlyVirtualProducts) {
      throw new Error('No shipping method selected');
    }

    const {
      method_id,
      label,
      cost,
      taxes,
    }: Pick<ICartShippingMethod, 'method_id' | 'label' | 'cost' | 'taxes'> =
      hasOnlyVirtualProducts ? noDeliveryMethod : selectedShippingMethod!;

    const shippingMethod: ISubmitOrderShippingLine = {
      method_id,
      method_title: label,
      total: `${cost ? parseFloat(cost) : 0}`,
      taxes,
    };

    const formData: ISubmitOrder = await mapCheckoutFieldsToOrder(
      values,
      shippingMethod,
      authUser,
      coupons,
      hasOnlyVirtualProducts
    );

    const {
      id: orderId,
      billing,
      shipping,
    } = await handleOrderCreation(formData);

    if (isOptedIn) {
      await handleMailchimpSignup(billing);
    }

    if (isPaymentRequired) {
      window?.sessionStorage.removeItem(GHBC_CHECKOUT_FORM);
      let paymentIntent: IIntentResponse | undefined;

      if (isFullStripeIntegration) {
        paymentIntent = await getPaymentIntent(
          values,
          cartLineItems,
          shippingMethod,
          hasOnlyVirtualProducts,
          orderId
        );
      }

      if (values.save_card && isAuthEnabled) {
        await handleSaveCard(
          values,
          hasOnlyVirtualProducts,
          stripe,
          elements,
          shipping,
          billing
        );
      }

      if (hasSubscriptionProds) {
        await handleSubscriptions(
          processSubscription,
          getSubscriptionProducts(cartLineItems),
          authUser,
          stripe,
          elements,
          shipping,
          billing
        );
      }

      return await handleCheckoutFormPayment(
        cartLineItems,
        shippingMethod,
        props,
        {
          email: billing?.email || authUser?.email,
          orderId,
          customer_name: `${billing?.first_name} ${billing?.last_name}`,
          billing,
          shipping,
          paymentMethod: values.savedPaymentMethod,
          paymentIntent: paymentIntent?.client_secret,
          elements,
          stripe,
          user,
        },
        setFieldError,
        setStatus
      );
    }

    await processOrder(String(orderId));
    Router.push(
      `/${EPageType.Checkout}/${EPageType.OrderConfirmation}?order_id_success=${orderId}&user=${user}&session_id=free_order`
    );
    return;
  } catch (error) {
    const { response = {} } = error;
    const { data: message } = response;
    Sentry.captureException(error);
    setFieldError(errorMessageField, message);
  }
};
